import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './Button';

/**
 * Diálogo modal del sistema de diseño.
 *
 * Reutiliza el lenguaje visual del panel: velo oscuro (`bg-ink/40`, el mismo del drawer
 * lateral del shell), tarjeta blanca con el radio y la sombra de `Card`, título de 16px y
 * acciones abajo a la derecha.
 *
 * Decisiones:
 * - __Sin `createPortal`__: se posiciona con `fixed`. Los scripts de smoke renderizan en
 *   `react-dom/server` (que no soporta portales) y, de paso, el diálogo queda dentro del
 *   árbol de la pantalla que lo abre (más fácil de leer al depurar).
 * - Cierra con Escape, con el botón de la equis y con un click en el velo.
 * - Bloquea el scroll del `body` mientras está abierto y marca `aria-modal` para que los
 *   lectores de pantalla no lean el contenido de atrás.
 * - Monta y desmonta el contenido (no solo lo oculta): así cada apertura arranca con los
 *   campos limpios y los mensajes de error de la vez anterior no sobreviven.
 */

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Explicación corta debajo del título. */
  description?: ReactNode;
  /** Acciones del pie (normalmente un `Button` secundario + uno primario). */
  footer?: ReactNode;
  /** `md` = formularios de un campo por fila; `lg` = formularios de dos columnas. */
  size?: 'md' | 'lg';
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto p-4 sm:items-center sm:p-6">
      {/* Velo: cierra al hacer click (mismo tono que el drawer del sidebar). */}
      <div
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 bg-ink/40 backdrop-blur-[1px]"
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'relative w-full rounded-card border border-line bg-surface shadow-drawer outline-none',
          size === 'lg' ? 'max-w-2xl' : 'max-w-lg',
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
          <div className="min-w-0">
            <h2 className="text-card-title font-semibold text-ink">{title}</h2>
            {description ? <p className="mt-1 text-caption text-ink-muted">{description}</p> : null}
          </div>

          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </Button>
        </div>

        <div className="scroll-thin max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-6 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
